import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const EMPTY_COMPANY = { name: "", code: "", contact_person: "", contact_email: "", contact_phone: "", address: "", website: "", is_placeholder: false };

export function CompanyDialog({ dialog, onClose, onCreated }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY);

  useEffect(() => {
    if (dialog === "new") setCompanyForm(EMPTY_COMPANY);
    else if (dialog) setCompanyForm({ name: dialog.name, code: dialog.code || "", contact_person: dialog.contact_person || "", contact_email: dialog.contact_email || "", contact_phone: dialog.contact_phone || "", address: dialog.address || "", website: dialog.website || "", is_placeholder: !!dialog.is_placeholder });
  }, [dialog]);

  const companySaveMut = useMutation({
    mutationFn: async () =>
      dialog === "new"
        ? (await api.post("/admin/companies", companyForm)).data
        : (await api.put(`/admin/companies/${dialog.id}`, companyForm)).data,
    onSuccess: (d) => {
      toast.success(dialog === "new" ? t("admin.toastCompanyCreated") : t("admin.toastCompanyUpdated"));
      if (dialog === "new" && d?.id) onCreated?.(d.id);
      onClose();
      setCompanyForm(EMPTY_COMPANY);
      qc.invalidateQueries({ queryKey: ["admin-companies"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="company-dialog">
        <DialogHeader><DialogTitle>{dialog === "new" ? t("admin.newCompanyTitle") : t("admin.editCompanyTitle", { name: dialog?.code || dialog?.name })}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("admin.legalNameL")}</Label>
            <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} data-testid="company-name-input" placeholder="e.g. Leyu Acrylics Ltd." />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.shortCodeL")}</Label>
            <Input value={companyForm.code} onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value })} className="font-mono" data-testid="company-code-input" placeholder="e.g. Leyu" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.contactPerson")}</Label>
            <Input value={companyForm.contact_person} onChange={(e) => setCompanyForm({ ...companyForm, contact_person: e.target.value })} data-testid="company-contact-person-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.contactEmail")}</Label>
            <Input type="email" value={companyForm.contact_email} onChange={(e) => setCompanyForm({ ...companyForm, contact_email: e.target.value })} data-testid="company-contact-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.contactPhone")}</Label>
            <Input value={companyForm.contact_phone} onChange={(e) => setCompanyForm({ ...companyForm, contact_phone: e.target.value })} data-testid="company-contact-phone-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("admin.address")}</Label>
            <Input value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} data-testid="company-address-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("admin.website")}</Label>
            <Input value={companyForm.website} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} data-testid="company-website-input" placeholder="https://…" />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <Checkbox id="placeholder" checked={companyForm.is_placeholder} onCheckedChange={(v) => setCompanyForm({ ...companyForm, is_placeholder: !!v })} data-testid="company-placeholder-checkbox" />
            <Label htmlFor="placeholder" className="font-normal">{t("admin.placeholderCompanyL")}</Label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => companySaveMut.mutate()} disabled={!companyForm.name.trim() || !companyForm.code.trim() || companySaveMut.isPending} data-testid="save-company-submit-button">
            {companySaveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {dialog === "new" ? t("common.create") : t("common.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
